/**
 * Showcase stage-time rules.
 * - File length cap is per piece (3:15).
 * - 5½ minutes is per dancer, after splitting group tracks evenly
 *   among IDs on piece.dancers (not free-text names).
 */

export const MAX_MUSIC_SECONDS = 195 // 3:15 per file
export const MAX_TOTAL_MUSIC_SECONDS = 330 // 5:30 per dancer
export const MAX_SOLO_PIECES = 3

export function isSoloPiece (piece) {
  const g = String((piece && piece.groupSize) || '').trim()
  return !g || g === 'Solo'
}

export function soloPieceCount (pieces) {
  let n = 0
  for (const piece of pieces || []) {
    if (!piece || piece.canceledByDancer) {
      continue
    }
    if (isSoloPiece(piece)) {
      n += 1
    }
  }
  return n
}

export function pieceDancerIds (piece) {
  const ids = Array.isArray(piece && piece.dancers) ? piece.dancers : []
  const out = []
  const seen = new Set()
  for (const raw of ids) {
    const id = String(raw || '').trim()
    if (!id || seen.has(id)) {
      continue
    }
    seen.add(id)
    out.push(id)
  }
  return out
}

export function pieceCastCount (piece) {
  return Math.max(1, pieceDancerIds(piece).length)
}

export function stageSecondsShare (piece) {
  const total = Math.max(0, Number(piece && piece.lengthInSeconds) || 0)
  if (total <= 0) {
    return 0
  }
  return total / pieceCastCount(piece)
}

export function stageSecondsForDancer (dancerId, pieces) {
  const id = String(dancerId || '').trim()
  if (!id) {
    return 0
  }
  let sum = 0
  for (const piece of pieces || []) {
    if (!piece || piece.canceledByDancer) {
      continue
    }
    if (pieceDancerIds(piece).indexOf(id) === -1) {
      continue
    }
    sum += stageSecondsShare(piece)
  }
  return sum
}

export function formatMusicTime (seconds) {
  const s = Math.max(0, Math.round(Number(seconds) || 0))
  const m = Math.floor(s / 60)
  const r = s % 60
  return m + ':' + String(r).padStart(2, '0')
}

export function pieceTimeLabelForDancer (piece, dancerId) {
  const total = Math.max(0, Number(piece && piece.lengthInSeconds) || 0)
  if (total <= 0) {
    return 'Length TBD'
  }
  const n = pieceCastCount(piece)
  if (n <= 1) {
    return formatMusicTime(total)
  }
  const share = stageSecondsShare(piece)
  return formatMusicTime(total) + ' total · ' + formatMusicTime(share) + ' of your 5½'
}

export function mergeProposedPiece (pieces, proposedPiece) {
  if (!proposedPiece) {
    return (pieces || []).slice()
  }
  const id = proposedPiece.id
  let found = false
  const next = (pieces || []).map((p) => {
    if (id && p && p.id === id) {
      found = true
      return { ...p, ...proposedPiece }
    }
    return p
  })
  if (!found) {
    next.push(proposedPiece)
  }
  return next.filter((p) => p && !p.canceledByDancer)
}

/**
 * @param {{ allPieces: Array, proposedPiece: object, nameForId?: (id: string) => string }} opts
 * @returns {{ ok: true } | { ok: false, error: string }}
 */
export function assertStageTimeOk ({ allPieces, proposedPiece, nameForId } = {}) {
  const fileSec = Math.max(0, Number(proposedPiece && proposedPiece.lengthInSeconds) || 0)
  if (fileSec > MAX_MUSIC_SECONDS) {
    return {
      ok: false,
      error: 'This file is ' + formatMusicTime(fileSec) +
        '. Each piece must be 3:15 or less — please trim and try again.'
    }
  }
  if (fileSec <= 0) {
    return { ok: true }
  }
  const next = mergeProposedPiece(allPieces, proposedPiece)
  const ids = pieceDancerIds(proposedPiece)
  const over = []
  for (const id of ids) {
    const used = stageSecondsForDancer(id, next)
    if (used > MAX_TOTAL_MUSIC_SECONDS + 0.05) {
      over.push({ id, used })
    }
  }
  if (!over.length) {
    return { ok: true }
  }
  const n = pieceCastCount(proposedPiece)
  const share = stageSecondsShare(proposedPiece)
  const names = over.map((o) => {
    const named = nameForId && nameForId(o.id)
    return named || 'a dancer'
  }).join(', ')
  const shareBit = n > 1
    ? 'This piece is ' + formatMusicTime(fileSec) +
      ' split ' + n + ' ways (' + formatMusicTime(share) + ' each). '
    : ''
  return {
    ok: false,
    error: shareBit +
      'That would put ' + names + ' over 5½ minutes on stage. ' +
      'Please trim the music or change the cast.'
  }
}
